import type { Difficulty, MCQQuestion } from './types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function rangeForDifficulty(d: Difficulty): { min: number; max: number } {
  if (d === 1) return { min: 10, max: 29 };
  if (d === 2) return { min: 30, max: 49 };
  return { min: 80, max: 99 };
}

export function generateMul2x1(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);
  const { min, max } = rangeForDifficulty(difficulty);
  const a = Math.floor(rand() * (max - min + 1)) + min;
  // d3: concentrate b in 5-9 for harder problems; d1/d2: full 1-9 range
  const bMin = difficulty === 3 ? 5 : 1;
  const b = Math.floor(rand() * (10 - bMin)) + bMin; // d3: 5..9, others: 1..9
  const correct = a * b;

  // Distractors model typical 3rd-grade mistakes
  const distractors = new Set<number>();
  // 1. Forgot the carry in tens place: a*b but no carry from ones
  const onesProduct = (a % 10) * b;
  const carry = Math.floor(onesProduct / 10);
  const noCarry = Math.floor(a / 10) * b * 10 + (onesProduct % 10);
  if (noCarry !== correct && noCarry > 0) distractors.add(noCarry);
  // 2. Forgot to multiply tens place: just a*b for the ones digit
  const onesOnly = (a % 10) * b;
  if (onesOnly !== correct && onesOnly > 0) distractors.add(onesOnly);
  // 3. Off-by-one carry
  const carryPlus = correct + 10;
  if (carryPlus !== correct) distractors.add(carryPlus);

  // Fill any missing distractor with random ±5..20
  while (distractors.size < 3) {
    const offset = Math.floor(rand() * 30) - 15;
    const candidate = correct + (offset === 0 ? 7 : offset);
    if (candidate > 0 && candidate !== correct && !distractors.has(candidate)) {
      distractors.add(candidate);
    }
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  // Deterministic shuffle via seed
  const shuffled = [...optionList].sort((x, y) => {
    const px = mulberry32(seed + x)();
    const py = mulberry32(seed + y)();
    return px - py;
  });

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `個位 ${a % 10}×${b}=${onesProduct};十位 ${Math.floor(a / 10)}×${b}${carry > 0 ? `,加進位 ${carry}` : ''} → ${correct}`,
    kp: 'math.mul.2x1',
  };
}
